import { useState, useEffect, useCallback } from 'react';
import { useParams } from "react-router-dom";

import Gallery from 'components/common/Gallery';
import { YoutubeVideoPlayer } from '../../../common/YoutubeVideoPlayer';
import * as eventApis from 'apis/events';

function getYouTubeVideoId(url) {
  if (!url) return null;
  const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

function formatDateTime(date) {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const EventDetails = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadEvent = useCallback(async () => {
    try {
      const response = await eventApis.fetchUserEvents();
      const found = (response.data || []).find((e) => String(e.id) === String(id));
      setEvent(found || null);
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  const handleRegister = async () => {
    setActionLoading(true);
    setMessage(null);
    try {
      await eventApis.registerForEvent(id);
      await loadEvent();
      setMessage({ type: 'success', text: 'Registration successful.' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Registration failed.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setMessage(null);
    try {
      await eventApis.cancelRegistration(id);
      await loadEvent();
      setMessage({ type: 'success', text: 'Registration cancelled.' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Cancellation failed.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (!event) {
    return (
      <div className="container mt-400">
        <div className="fs-200 ff-inter-light">Loading event...</div>
      </div>
    );
  }

  const renderActionArea = () => {
    if (event.type === 'external') {
      return event.externalLink ? (
        <a className="btn-primary" href={event.externalLink} target="_blank" rel="noopener noreferrer">
          Visit Event
        </a>
      ) : null;
    }

    if (event.type === 'standard') {
      return (
        <div className="fs-200 ff-inter-light">
          Active members are automatically entered into this event&apos;s draw.
        </div>
      );
    }

    // registration type
    if (event.isRegistered) {
      return (
        <div>
          <div className="fs-200 ff-inter-semibold text-green mb-150">You are registered for this event.</div>
          <button className="btn-primary" onClick={handleCancel} disabled={actionLoading}>
            {actionLoading ? 'Please wait...' : 'Cancel Registration'}
          </button>
        </div>
      );
    }

    if (event.isWaitlisted) {
      return (
        <div>
          <div className="fs-200 ff-inter-semibold text-bright-red mb-150">
            You are on the waiting list for this event.
          </div>
          <button className="btn-primary" onClick={handleCancel} disabled={actionLoading}>
            {actionLoading ? 'Please wait...' : 'Leave Waitlist'}
          </button>
        </div>
      );
    }

    if (event.status === 'closed' || event.status === 'completed') {
      return <div className="fs-200 ff-inter-light">Registration is closed for this event.</div>;
    }

    return (
      <button className="btn-primary" onClick={handleRegister} disabled={actionLoading}>
        {actionLoading ? 'Please wait...' : 'Register'}
      </button>
    );
  };

  return (
    <div className="container mt-300">
      <div className="row mb-300">
        <div className="col-12 col-md-7 pr-md-300">
          <div className="mb-300">
            <Gallery images={event?.images} />
          </div>

          <h2 className="fs-400 ff-inter-semibold mb-200">
            {[event.firstName, event.lastName].filter(Boolean).join(' ')}
          </h2>

          <div className="fs-200 ff-inter-medium mb-100">Description:</div>
          <div className="fs-200 mb-300 ff-inter-light">{event.description}</div>

          {event.youtubeLink && (
            <div className="my-200">
              <YoutubeVideoPlayer embedId={getYouTubeVideoId(event.youtubeLink)} />
            </div>
          )}

          {event.termsAndConditions && (
            <div className="mb-300">
              <div className="fs-200 ff-inter-medium mb-100">Terms &amp; Conditions:</div>
              <div className="fs-150 ff-inter-light">{event.termsAndConditions}</div>
            </div>
          )}
        </div>

        <div className="col-12 col-md-5">
          <div className="row fs-200 ff-inter-semibold m-none mb-200">
            {event.eventDate && (
              <div className="col-md-6">
                <div className="fs-200 ff-inter-light">Event Date</div>
                {formatDateTime(event.eventDate)}
                <div className="fs-150 fs-italic ff-inter-light">Indian Standard Time</div>
              </div>
            )}
            {event.location && (
              <div className="col-md-6">
                <div className="fs-200 ff-inter-light">Location</div>
                {event.location}
              </div>
            )}
          </div>

          {event.type === 'registration' && (
            <div className="row fs-200 ff-inter-semibold m-none mb-200">
              {event.registrationCloseDate && (
                <div className="col-md-6">
                  <div className="fs-200 ff-inter-light">Registration Closes</div>
                  {formatDateTime(event.registrationCloseDate)}
                </div>
              )}
              {event.maxParticipants != null && (
                <div className="col-md-6">
                  <div className="fs-200 ff-inter-light">Seats</div>
                  {event.participantCount || 0} / {event.maxParticipants}
                </div>
              )}
            </div>
          )}

          <div className="border my-200"></div>

          {message && (
            <div
              className={`fs-200 mb-200 ${
                message.type === 'success' ? 'text-green' : 'text-bright-red'
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="mt-200">{renderActionArea()}</div>
        </div>
      </div>
    </div>
  );
};

export default EventDetails;
